import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';
import { useTenantQuery } from './useTenantQuery';
import { useSocketFactory } from './useSocketFactory';
import { personnelTrackingKeys } from './usePersonnelTracking';
import { claimPersonnelRoom } from './personnelRoomClaims';
import {
  createInitialSosAlarmState,
  isRecordedSosSocketEvent,
  isSosSocketEvent,
  pruneAcks,
  sosAlarmReducer,
  type OpenSosRow,
  type SosAlarm,
} from '@/components/safety/sosAlarmModel';

/** Polling fallback cadence: an SOS reaches the console even with the socket down. */
export const SOS_POLL_INTERVAL_MS = 10_000;

/**
 * Minimum gap between socket-driven server checks (SOS events and reconnects).
 * The first check runs at once; anything inside the window collapses into ONE
 * trailing check. Without this, a burst of broadcasts would make every console
 * in the tenant hit the server once per event.
 */
export const SOS_RECONCILE_MIN_GAP_MS = 2_000;

const ACK_STORAGE_PREFIX = 'cmdbridge:sos-alarm-acks:v1:';

/**
 * Acknowledgements are a per-console convenience: without them a reload would
 * re-alarm every SOS this operator already acknowledged (sessions stay in
 * emergency status until the responder's session ends). Storage can be
 * unavailable (private mode, blocked site data) — the alarm must still work,
 * so every access is guarded and failure just means "nothing remembered".
 */
function loadAcks(tenantId: string | null): Record<string, number> {
  if (!tenantId) return {};
  try {
    const raw = window.localStorage.getItem(ACK_STORAGE_PREFIX + tenantId);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return pruneAcks(parsed as Record<string, number>, Date.now());
  } catch {
    return {};
  }
}

function saveAcks(tenantId: string | null, acked: Record<string, number>): void {
  if (!tenantId) return;
  try {
    window.localStorage.setItem(ACK_STORAGE_PREFIX + tenantId, JSON.stringify(acked));
  } catch {
    // Storage full or blocked: acknowledgements simply aren't remembered.
  }
}

interface OpenSosResponse {
  data: OpenSosRow[];
}

export interface UseSosAlarmResult {
  /** Unacknowledged SOS alarms, one per responder session, in arrival order. */
  alarms: SosAlarm[];
  /** Acknowledge (silence) one responder's alarm on THIS console. */
  acknowledge: (sessionId: string) => void;
  /** The real-time /map socket is connected. */
  socketConnected: boolean;
  /** The 10 s polling fallback is running (requires personnel_tracking:view). */
  pollingEnabled: boolean;
  /**
   * The viewer may see responder positions (personnel_tracking:view, the same
   * permission the polling endpoint enforces). Without it the alarm still
   * names the responder and the time, but not where they are.
   */
  canViewLocation: boolean;
  /** The most recent poll failed. */
  pollFailing: boolean;
}

/**
 * App-shell SOS alarm state (P0-14).
 *
 * Two independent paths feed one deduplicated alarm list:
 *  1. `personnel:sos-alert` on a dedicated /map socket — immediate, but only
 *     while the socket is up;
 *  2. GET /personnel-tracking/sos/open every 10 s (also in background tabs:
 *     EOC wall displays park pages in hidden tabs) — the fallback that still
 *     raises an SOS when the socket is down, and the reconciliation step run
 *     immediately on every socket reconnect.
 *
 * A socket event raises an alarm by itself only when it carries the
 * server-issued SOS event id; otherwise it is just a hint to check the server
 * (see sosAlarmModel.ts). Alarms leave the list only through `acknowledge`.
 */
export function useSosAlarm(): UseSosAlarmResult {
  const tenantId = useTenantStore((s) => s.activeTenant?.id ?? null);
  const canViewPersonnel = useAuthStore(
    (s) =>
      !!s.user?.is_platform_admin ||
      s.permissions.some((p) => p.module === 'personnel_tracking' && p.action === 'view'),
  );
  const pollingEnabled = canViewPersonnel;

  const [state, dispatch] = useReducer(sosAlarmReducer, tenantId, (t) =>
    createInitialSosAlarmState(loadAcks(t)),
  );

  // Alarm state is per tenant: switching tenants starts from that tenant's
  // remembered acknowledgements, and its open SOSes arrive via the next poll.
  const stateTenantRef = useRef(tenantId);
  useEffect(() => {
    if (stateTenantRef.current === tenantId) return;
    stateTenantRef.current = tenantId;
    dispatch({ type: 'reset', acked: loadAcks(tenantId) });
  }, [tenantId]);

  // Persist acknowledgements for the tenant they belong to.
  const isFirstAckSave = useRef(true);
  useEffect(() => {
    if (isFirstAckSave.current) {
      isFirstAckSave.current = false;
      return;
    }
    saveAcks(stateTenantRef.current, state.acked);
  }, [state.acked]);

  // -- Path 2: polling fallback ----------------------------------------------
  const openSos = useTenantQuery<OpenSosResponse>(
    personnelTrackingKeys.openSos(),
    '/personnel-tracking/sos/open',
    {
      params: { include_exercise: 'true' },
      staleTime: 0,
      enabled: pollingEnabled,
      queryOptions: {
        refetchInterval: SOS_POLL_INTERVAL_MS,
        refetchIntervalInBackground: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // The next poll is at most 10 s away; retries would only delay it.
        retry: false,
      },
    },
  );

  const { data: openSosData, dataUpdatedAt, refetch } = openSos;
  useEffect(() => {
    if (!openSosData) return;
    const rows = Array.isArray(openSosData.data) ? openSosData.data : [];
    dispatch({ type: 'poll', rows, now: Date.now() });
  }, [openSosData, dataUpdatedAt]);

  // Socket-driven server check, throttled (leading edge + one trailing call).
  const lastReconcileAtRef = useRef(Number.NEGATIVE_INFINITY);
  const trailingReconcileRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconcile = useCallback(() => {
    if (!pollingEnabled) return;
    const wait = lastReconcileAtRef.current + SOS_RECONCILE_MIN_GAP_MS - Date.now();
    if (wait <= 0) {
      lastReconcileAtRef.current = Date.now();
      void refetch();
      return;
    }
    if (trailingReconcileRef.current !== null) return; // one trailing check already queued
    trailingReconcileRef.current = setTimeout(() => {
      trailingReconcileRef.current = null;
      lastReconcileAtRef.current = Date.now();
      void refetch();
    }, wait);
  }, [pollingEnabled, refetch]);

  useEffect(
    () => () => {
      if (trailingReconcileRef.current !== null) clearTimeout(trailingReconcileRef.current);
    },
    [],
  );

  // -- Path 1: real-time socket ----------------------------------------------
  const handleSosEvent = useCallback(
    (payload: unknown) => {
      if (!isSosSocketEvent(payload)) return;
      // Defence in depth: the server scopes the room by tenant; never raise
      // another tenant's SOS if a payload ever says otherwise.
      if (payload.tenantId && tenantId && payload.tenantId !== tenantId) return;
      // Only a server-recorded SOS (it carries event_id) alarms straight from
      // the socket. Anything else is a hint: the check below raises it only if
      // the server lists the session as in emergency.
      if (isRecordedSosSocketEvent(payload)) {
        dispatch({ type: 'socket', event: payload, now: Date.now() });
      }
      // Pull the authoritative rows (drill flag, last position) right away.
      reconcile();
    },
    [tenantId, reconcile],
  );

  // Every /map socket hears the SOS alarm (the server's alarm room). Only a
  // socket in the personnel room (personnel_tracking:view, checked by the
  // server) hears where the responder is (P0-13 purpose-scoped rooms).
  const { isConnected: socketConnected, socketRef } = useSocketFactory({
    namespace: '/map',
    displayName: 'SOS alarm',
    // The alarm modal reports its own link state; no connect/disconnect toasts.
    showToasts: false,
    enabled: !!tenantId,
    extraAuth: tenantId ? { tenantId } : undefined,
    events: { 'personnel:sos-alert': handleSosEvent },
    // Anything raised while the socket was down is picked up immediately.
    onReconnect: reconcile,
  });

  // Hold the personnel room while connected AND allowed to see positions. An
  // effect, not onConnect (R10-FIX, wave-10 residuals 106/155): it re-runs on
  // every (re)connect, and also when personnel_tracking:view arrives or is
  // granted AFTER the socket connected (first login before permissions load),
  // so a cleared console never sits on the presence-only SOS with a misleading
  // "no location". A claim, not a bare join: the dashboards' usePersonnelSocket
  // shares this socket, and its leave must not take the alarm out of the room.
  useEffect(() => {
    const socket = socketRef.current;
    if (!socketConnected || !canViewPersonnel || !socket) return undefined;
    return claimPersonnelRoom(socket);
  }, [socketConnected, canViewPersonnel, socketRef]);

  const acknowledge = useCallback((sessionId: string) => {
    dispatch({ type: 'ack', sessionId, now: Date.now() });
  }, []);

  return {
    alarms: state.alarms,
    acknowledge,
    socketConnected,
    pollingEnabled,
    canViewLocation: canViewPersonnel,
    pollFailing: pollingEnabled && openSos.isError,
  };
}
